import {
  Compiler,
  ComponentFactory,
  ComponentFactoryResolver,
  ComponentRef,
  Injectable,
  Injector,
  NgModuleFactory,
  NgModuleRef
} from '@angular/core';
import { DynamicModuleRegistry } from './dynamic-content-module-loader.registry';
import { DynamicContentOutletErrorComponent } from './dynamic-content-outlet-error.component';
import { DynamicContentOutletRegistry } from './dynamic-content-outlet.registry';
import { ModuleWithDynamicComponentsMap } from './module-with-dynamic-components-map';

@Injectable({ providedIn: 'root' })
export class DynamicContentOutletService {
  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private compiler: Compiler
  ) {}

  async GetComponent(componentName: string): Promise<ComponentRef<any>> {
    const dynamicModuleFactory: NgModuleFactory<any> = await this.GetModule(componentName);

    if (dynamicModuleFactory) {
      try {
        const dynamicModuleRef: NgModuleRef<any> = dynamicModuleFactory.create(this.injector);
        const componentResolver: ComponentFactoryResolver = dynamicModuleRef.componentFactoryResolver;
        const componentType = (dynamicModuleFactory.moduleType as ModuleWithDynamicComponentsMap).dynamicComponentsMap[
          componentName
        ];
        const componentFactory = componentResolver.resolveComponentFactory(componentType);
        return componentFactory.create(this.injector);
      } catch (error) {
        console.error(error.message);
        return this.getDynamicContentErrorComponent(
          `Unable to load component: ${componentName} from module factory.
          Error Details: ${error.message}`
        );
      }
    } else {
      return this.getDynamicContentErrorComponent(`Unable to load module factory for component: ${componentName}.`);
    }
  }

  async GetComponentFactory(componentName: string): Promise<ComponentFactory<any>> {
    const dynamicModuleFactory: NgModuleFactory<any> = await this.GetModule(componentName);

    if (dynamicModuleFactory) {
      try {
        const dynamicModuleRef: NgModuleRef<any> = dynamicModuleFactory.create(this.injector);
        const componentResolver: ComponentFactoryResolver = dynamicModuleRef.componentFactoryResolver;
        const componentType = (dynamicModuleFactory.moduleType as ModuleWithDynamicComponentsMap).dynamicComponentsMap[
          componentName
        ];
        const componentFactory = componentResolver.resolveComponentFactory(componentType);
        return componentFactory;
      } catch (error) {
        console.error(error.message);
        throw new Error(`Cannot retrieve component factory for component: ${componentName}`);
      }
    } else {
      throw new Error(`Cannot retrieve module factory for component: ${componentName}`);
    }
  }

  async GetModule(componentName: string): Promise<NgModuleFactory<any>> {
    const moduleInfo = this.getModuleInfoFromRegistry(componentName);

    if (!moduleInfo) {
      throw new Error(`Cannot retrieve module info for ${componentName}`);
    }

    const moduleName = moduleInfo.moduleName.charAt(0).toLowerCase() + moduleInfo.moduleName.slice(1);

    try {
      return DynamicModuleRegistry[moduleName]
        .loadChildren()
        .then((moduleOrFactory) => {
          if (moduleOrFactory instanceof NgModuleFactory) {
            return moduleOrFactory;
          } else {
            return this.compiler.compileModuleAsync(moduleOrFactory);
          }
        })
        .then((factory: NgModuleFactory<any>) => {
          return factory;
        });
    } catch (error) {
      console.error(error.message);
    }
  }

  public getModuleInfoFromRegistry(componentName: string) {
    const registryItem = DynamicContentOutletRegistry.find((i) => i.componentName === componentName);

    if (registryItem) {
      return registryItem;
    }

    return null;
  }

  private getDynamicContentErrorComponent(errorMessage: string) {
    const factory = this.componentFactoryResolver.resolveComponentFactory(DynamicContentOutletErrorComponent);
    const componentRef = factory.create(this.injector);
    const instance = <any>componentRef.instance;
    instance.errorMessage = errorMessage;
    return componentRef;
  }
}
